// A medicine named by its code alone (spec 054 T099). An imported row carries
// only the nomenclator code the file held, so the report and the dialog showed
// `E2E054M01` where a name belongs. The name comes from the lookup the dialog
// already makes, `GET /stock/report-search` (contracts/bff-stock-reporting.md):
// it matches codes, finds inactive medicines too, and answers the same label and
// identity a searched medicine is shown with. Only an exact code is taken; a
// sibling code that merely starts the same is another medicine.
//
// What it resolves is display only. The draft is not rewritten behind the
// operator's back (constitution art. IV); an edited row carries the name when
// the operator saves it.
import { useQueries, useQuery } from '@tanstack/react-query';
import { REPORT_SEARCH_MIN_LENGTH, searchReport, type ReportSearchHit } from './report-search-api';

// Lower case, no accents, no spacing: "300MG" is "300 mg", "Soluție" is "solutie".
function compact(value: string): string {
  return value.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, '');
}

function stated(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * A search result's title (spec 054 T106): the brand, then the dose and the
 * form it does not already carry. Built from the structured fields, not from
 * `medicineLabel`, which joins them all and said "300 mg capsule" twice for a
 * brand named "Adaptol® 300 mg capsule".
 */
export function medicineTitle(
  hit: Pick<ReportSearchHit, 'name' | 'dose' | 'form'> & { medicineLabel?: string },
): string {
  const name = stated(hit.name) ? hit.name.trim() : (hit.medicineLabel ?? '').trim();
  const parts = [name];
  let said = compact(name);
  for (const part of [hit.dose, hit.form]) {
    if (!stated(part)) continue;
    const key = compact(part);
    if (said.includes(key)) continue;
    parts.push(part.trim());
    said += key;
  }
  return parts.filter((part) => part.length > 0).join(' ');
}

/**
 * The line under a result's title (spec 054 T106): code, DCI, producer, what
 * tells two like titles apart. Country and ATC wait for the identity panel;
 * a DCI that is already the title (a code with no brand) is not said twice.
 */
export function medicineSupportingLine(
  hit: Pick<ReportSearchHit, 'code' | 'name' | 'dci' | 'producer'> &
    Partial<Pick<ReportSearchHit, 'barcode' | 'matched'>>,
): string[] {
  const dci = stated(hit.dci) && compact(hit.dci) !== compact(hit.name) ? hit.dci : null;
  // The barcode is not part of the row, except when it is why the row matched:
  // a hit found by barcode alone would otherwise show nothing the operator typed.
  const barcode = hit.matched?.includes('barcode') && stated(hit.barcode) ? hit.barcode : null;
  return [hit.code, dci, hit.producer, barcode].filter(stated);
}

/** A status as a MUD tag says it: the label's key and the tag's style (mud/components/tag.md). */
export interface StatusTagSpec {
  key: string;
  semantic: 'success' | 'neutral' | 'accent' | 'danger';
}

/**
 * Which national list the code comes from, and whether its authorisation still
 * runs (spec 054 T109; caiet UC50 step 4). Said on its own tag, never folded
 * into the code's state: the two vary independently (UC20-10's grace period
 * keeps a lapsed authorisation active). MUD has no warning tag, and tag.md
 * says a cautionary status is Accent, so a lapsed authorisation is Accent;
 * "neautorizat" is a fact of the list, not a fault, so it is Neutral, and so is
 * "cod de bare", which is what a barcode is rather than a status of it.
 */
export function authorisationStatus(
  hit: Pick<ReportSearchHit, 'authorised' | 'authorisationExpiry' | 'kind'>,
  today: string,
): StatusTagSpec {
  // A barcode is marked as one: it has no list of its own to be authorised on
  // (BUG-1-044, TC-NC-UC50-05-01).
  if (hit.kind === 'BARCODE') return { key: 'medicine_combobox.barcode', semantic: 'neutral' };
  if (!hit.authorised) return { key: 'medicine_combobox.unauthorized', semantic: 'neutral' };
  const lapsed = stated(hit.authorisationExpiry) && hit.authorisationExpiry < today;
  return lapsed
    ? { key: 'stock_reporting.dialog.tag.authorisation_expired', semantic: 'accent' }
    : { key: 'stock_reporting.dialog.tag.authorised', semantic: 'success' };
}

/**
 * The code's state in SIMSM (spec 054 T109): inactive once it leaves the lists,
 * once UC20-10's grace period ends, or when the administrator says so, and then
 * it takes no new lot (UC50-01). "activ" is the rule, so it is the quiet Neutral
 * and does not compete with a green "autorizat" beside it; "inactiv" is Danger.
 */
export function medicineStateStatus(hit: Pick<ReportSearchHit, 'active'>): StatusTagSpec {
  return hit.active
    ? { key: 'stock_reporting.dialog.tag.active', semantic: 'neutral' }
    : { key: 'stock_reporting.dialog.tag.inactive', semantic: 'danger' };
}

/** A name changes with the nomenclator, not within a session. */
const IDENTITY_STALE_MS = 10 * 60_000;

export async function resolveMedicine(
  code: string,
  siteId: string | null,
): Promise<ReportSearchHit | null> {
  const { hits } = await searchReport(code, siteId);
  return hits.find((hit) => hit.code === code) ?? null;
}

function identityQuery(siteId: string | null, code: string) {
  return {
    queryKey: ['stock-medicine-identity', siteId ?? '', code] as const,
    // With no location the search answers from the catalogue alone, which is
    // all a name needs (owner decision, 2026-09-15).
    queryFn: () => resolveMedicine(code, siteId || null),
    // The search refuses a shorter query (QUERY_TOO_SHORT).
    enabled: code.length >= REPORT_SEARCH_MIN_LENGTH,
    staleTime: IDENTITY_STALE_MS,
  };
}

/** One code's identity, when `enabled`; null until known, and for a code the catalogue lacks. */
export function useMedicineIdentity(
  siteId: string | null,
  code: string,
  enabled: boolean,
): ReportSearchHit | null {
  const query = identityQuery(siteId, code);
  const { data } = useQuery({ ...query, enabled: enabled && query.enabled });
  return data ?? null;
}

/** The names of the codes given, as far as the catalogue answers; a code it does not know is absent. */
export function useMedicineNames(
  siteId: string | null,
  codes: readonly string[],
): ReadonlyMap<string, string> {
  const unique = [...new Set(codes)];
  const results = useQueries({ queries: unique.map((code) => identityQuery(siteId, code)) });
  const names = new Map<string, string>();
  unique.forEach((code, index) => {
    const label = results[index]?.data?.medicineLabel;
    if (label) names.set(code, label);
  });
  return names;
}
